import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faVideo, faComment, faHeart, faShare, faEye, faLink, faExpand, faTimes, faInfoCircle, faBrain, faLightbulb, faExclamationTriangle, faArrowTrendUp, faCheckCircle, faExclamationCircle, faClock, faChartLine, faUsers, faThumbsUp, faHashtag, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faTiktok } from '@fortawesome/free-brands-svg-icons';
import { useTikTok } from '../../context/TikTokContext';
import { useAuth } from '../../context/AuthContext';
import { creditsAPI, analysisJobsAPI, businessAuthAPI, tiktokAPI } from '../../services/api';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const TikTokPage: React.FC = () => {
  // @ts-expect-error - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { lang } = useParams<{ lang: string }>();
  const { business, refreshBusinessProfile } = useAuth();
  const { state, fetchAccountData, fetchRecentVideos, fetchTopVideos, fetchEngagementData, fetchReachData, fetchHashtagsData, fetchContentPerformanceData, fetchSentimentData, fetchProblemsData, fetchAIAnalysis } = useTikTok();
    const [aiModalOpen, setAiModalOpen] = useState(false);
    const [estimateLoading, setEstimateLoading] = useState(false);
    const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [selectedPeriod, setSelectedPeriod] = useState('30');
    const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
    const [modalChart, setModalChart] = useState<string | null>(null);
    
    // Account setup state
    const [username, setUsername] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [profile, setProfile] = useState<any>(null);
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Chart refs
    const engagementChartRef = useRef<HTMLCanvasElement>(null);
    const reachChartRef = useRef<HTMLCanvasElement>(null);
    const hashtagsChartRef = useRef<HTMLCanvasElement>(null);
    const contentPerformanceChartRef = useRef<HTMLCanvasElement>(null);
    const sentimentChartRef = useRef<HTMLCanvasElement>(null);

    // Modal chart refs
    const modalEngagementChartRef = useRef<HTMLCanvasElement>(null);
    const modalReachChartRef = useRef<HTMLCanvasElement>(null);
    const modalHashtagsChartRef = useRef<HTMLCanvasElement>(null);
    const modalContentPerformanceChartRef = useRef<HTMLCanvasElement>(null);
    const modalSentimentChartRef = useRef<HTMLCanvasElement>(null);

    // Chart instances
    const chartInstancesRef = useRef<{ [key: string]: ChartJS | null }>({});

    // Check if account exists and sync status
    const hasAccount = !!(business?.tiktok?.username || business?.tiktok?.isConnected);
    const syncStatus = business?.tiktok?.syncStatus;
    const isPending = syncStatus === 'pending';
    const isCompleted = syncStatus === 'completed';

    // Check analysis status from business model
    const analysisStatus = business?.analysisStatus?.['tiktok-analytics']?.status || 'noanalyze';
    const isAnalysisPending = analysisStatus === 'pending';
    const isAnalysisCompleted = analysisStatus === 'completed';
    const isAnalysisFailed = analysisStatus === 'failed';
    const hasAnyAnalysis = isAnalysisCompleted;

    // Component mount olduğunda verileri yükle - only if account exists, sync is completed and analysis is completed
    useEffect(() => {
        if (hasAccount && isCompleted && isAnalysisCompleted) {
        fetchAccountData();
        fetchRecentVideos();
        fetchTopVideos();
        fetchEngagementData();
        fetchReachData();
        fetchHashtagsData();
        fetchContentPerformanceData();
        fetchSentimentData();
        fetchProblemsData();
        fetchAIAnalysis('engagement');
        fetchAIAnalysis('reach');
        fetchAIAnalysis('hashtags');
        fetchAIAnalysis('contentPerformance');
        fetchAIAnalysis('sentiment');
        }
    }, [hasAccount, isCompleted, isAnalysisCompleted, fetchAccountData, fetchRecentVideos, fetchTopVideos, fetchEngagementData, fetchReachData, fetchHashtagsData, fetchContentPerformanceData, fetchSentimentData, fetchProblemsData, fetchAIAnalysis]);


    // Handle username verification
    const handleVerify = async () => {
        if (!username.trim()) {
            setVerifyError(t('socialMedia.tiktok.enterUsername'));
            return;
        }

        setVerifying(true);
        setVerifyError(null);
        setProfile(null);

        try {
            const response = await businessAuthAPI.getSocialMediaProfiles({ tiktok: username.trim() });
            const respData = response?.data?.data || {};
            const tiktokProfile = respData.tiktok;

            if (tiktokProfile) {
                setProfile(tiktokProfile);
                setVerifyError(null);
            } else {
                setVerifyError(t('socialMedia.tiktok.profileNotFound'));
                setProfile(null);
            }
        } catch (err: any) {
            const errorMsg = err?.response?.data?.errors?.tiktok || err?.response?.data?.message || t('socialMedia.tiktok.verificationFailed');
            setVerifyError(errorMsg);
            setProfile(null);
        } finally {
            setVerifying(false);
        }
    };

    // Handle account submission
    const handleSubmit = async () => {
        if (!profile) {
            setVerifyError(t('socialMedia.tiktok.verifyFirst'));
            return;
        }

        setSubmitting(true);
        setVerifyError(null);

        try {
            await businessAuthAPI.addSocialMediaAccounts({ tiktok: username.trim() });
            await refreshBusinessProfile();
            
            // Start sync in background
            try {
                void businessAuthAPI.syncSocialMediaData();
            } catch (_e) {
                // Ignore sync errors
            }
        } catch (err: any) {
            setVerifyError(err?.response?.data?.message || t('socialMedia.tiktok.accountAddError'));
        } finally {
            setSubmitting(false);
        }
    };

    // AI Analysis component
    const AIAnalysis: React.FC<{ chartType: string }> = ({ chartType }) => {
        const analysis = state.aiAnalysis[chartType];
        const isLoading = state.loading.aiAnalysis[chartType];
        const error = state.error.aiAnalysis[chartType];
        
        if (isLoading) {
            return (
                <div className="mt-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="text-white/70">{t('socialMedia.common.aiAnalysisLoading')}</div>
                    </div>
                </div>
            );
        }
        
        if (error === 'PREPARING') {
            return (
                <div className="mt-6">
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
                            <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                        </div>
                        <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.common.preparingAiAnalysis')}</p>
                        <p className="text-white/50 text-sm">{t('socialMedia.common.pleaseWait')}</p>
                    </div>
                </div>
            );
        }
        
        if (error) {
            return (
                <div className="mt-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="text-red-400">{t('socialMedia.common.aiAnalysisError')} {error}</div>
                    </div>
                </div>
            );
        }
        
        if (!analysis) return null;

        return (
            <div className="mt-6 space-y-3">
                <div className="flex items-center space-x-2 mb-4">
                    <FontAwesomeIcon icon={faBrain} className="text-purple-400 text-lg" />
                    <h4 className="text-lg font-semibold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h4>
                </div>
                <div className="space-y-3">
                    {analysis.insights.map((insight, index) => (
                        <div key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
                            <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    insight.type === 'insight' ? 'bg-blue-500/20' :
                                    insight.type === 'prediction' ? 'bg-green-500/20' :
                                    insight.type === 'recommendation' ? 'bg-purple-500/20' :
                                    insight.type === 'warning' ? 'bg-yellow-500/20' :
                                    'bg-gray-500/20'
                                }`}>
                                    <FontAwesomeIcon 
                                        icon={
                                            insight.type === 'insight' ? faLightbulb :
                                            insight.type === 'prediction' ? faArrowTrendUp :
                                            insight.type === 'recommendation' ? faBrain :
                                            insight.type === 'warning' ? faExclamationTriangle :
                                            faInfoCircle
                                        } 
                                        className={`text-sm ${
                                            insight.type === 'insight' ? 'text-blue-400' :
                                            insight.type === 'prediction' ? 'text-green-400' :
                                            insight.type === 'recommendation' ? 'text-purple-400' :
                                            insight.type === 'warning' ? 'text-yellow-400' :
                                            'text-gray-400'
                                        }`} 
                                    />
                                </div>
                                <div className="flex-1">
                                    <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                                    <p className="text-white/70 text-sm leading-relaxed">{insight.message}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Tooltip handlers
    const handleTooltipEnter = (tooltipId: string) => {
        setHoveredTooltip(tooltipId);
    };

    const handleTooltipLeave = () => {
        setHoveredTooltip(null);
    };

    // Engagement Chart
    useEffect(() => {
        if (engagementChartRef.current && state.engagement) {
            const ctx = engagementChartRef.current.getContext('2d');
            if (ctx) {
                if (chartInstancesRef.current.engagement) {
                    chartInstancesRef.current.engagement.destroy();
                }

                const chart = new ChartJS(ctx, {
                    type: 'line',
                    data: state.engagement,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                            intersect: false,
                            mode: 'index'
                        },
                        plugins: {
                            legend: {
                                labels: {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    font: {
                                        size: 12
                                    }
                                }
                            }
                        },
                        scales: {
                            x: {
                                grid: {
                                    color: 'rgba(255, 255, 255, 0.1)'
                                },
                                ticks: {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    font: {
                                        size: 11
                                    }
                                }
                            },
                            y: {
                                grid: {
                                    color: 'rgba(255, 255, 255, 0.1)'
                                },
                                ticks: {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    font: {
                                        size: 11
                                    },
                                    callback: function (value) {
                                        return value + '%';
                                    }
                                }
                            }
                        }
                    }
                });

                chartInstancesRef.current.engagement = chart;
            }
        }
    }, [state.engagement]);

    // Reach Chart
    useEffect(() => {
        if (reachChartRef.current && state.reach) {
            const ctx = reachChartRef.current.getContext('2d');
            if (ctx) {
                if (chartInstancesRef.current.reach) {
                    chartInstancesRef.current.reach.destroy();
                }

                const normalizedReachData = {
                    labels: state.reach.labels || [],
                    datasets: (state.reach.datasets || []).map((dataset: any, index: number) => {
                        const dataArray = Array.isArray(dataset.data)
                            ? dataset.data.map((value: any) => (typeof value === 'number' ? value : parseFloat(value) || 0))
                            : typeof dataset.data === 'number'
                                ? [dataset.data]
                                : [];

                        const defaultColors = [
                            'rgba(255, 0, 80, 0.7)',
                            'rgba(59, 130, 246, 0.7)',
                            'rgba(16, 185, 129, 0.7)',
                            'rgba(236, 72, 153, 0.7)',
                            'rgba(251, 191, 36, 0.7)',
                            'rgba(167, 139, 250, 0.7)',
                        ];

                        return {
                            ...dataset,
                            data: dataArray,
                            backgroundColor: dataset.backgroundColor ?? defaultColors[index % defaultColors.length],
                            borderColor: dataset.borderColor ?? '#FF0050',
                            borderWidth: dataset.borderWidth ?? 2,
                        };
                    }),
                };

                const chart = new ChartJS(ctx, {
                    type: 'bar',
                    data: normalizedReachData,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                labels: {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    font: {
                                        size: 12
                                    }
                                }
                            }
                        },
                        scales: {
                            x: {
                                grid: {
                                    color: 'rgba(255, 255, 255, 0.1)'
                                },
                                ticks: {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    font: {
                                        size: 11
                                    }
                                }
                            },
                            y: {
                                grid: {
                                    color: 'rgba(255, 255, 255, 0.1)'
                                },
                                ticks: {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    font: {
                                        size: 11
                                    },
                                    callback: function (value) {
                                        return (Number(value) / 1000).toFixed(0) + 'K';
                                    }
                                }
                            }
                        }
                    }
                });

                chartInstancesRef.current.reach = chart;
            }
        }
    }, [state.reach]);

    // Hashtags Chart
    useEffect(() => {
        if (hashtagsChartRef.current && state.hashtags) {
            const ctx = hashtagsChartRef.current.getContext('2d');
            if (ctx) {
                if (chartInstancesRef.current.hashtags) {
                    chartInstancesRef.current.hashtags.destroy();
                }

                const chart = new ChartJS(ctx, {
                    type: 'bar',
                    data: state.hashtags,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'bottom',
                                labels: {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    font: {
                                        size: 12
                                    },
                                    padding: 20
                                }
                            }
                        }
                    }
                });

                chartInstancesRef.current.hashtags = chart;
            }
        }
    }, [state.hashtags]);

    // Content Performance Chart
    useEffect(() => {
        if (contentPerformanceChartRef.current && state.contentPerformance) {
            const ctx = contentPerformanceChartRef.current.getContext('2d');
            if (ctx) {
                if (chartInstancesRef.current.contentPerformance) {
                    chartInstancesRef.current.contentPerformance.destroy();
                }

                const chart = new ChartJS(ctx, {
                    type: 'doughnut',
                    data: state.contentPerformance,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'bottom',
                                labels: {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    font: {
                                        size: 12
                                    }
                                }
                            }
                        }
                    }
                });

                chartInstancesRef.current.contentPerformance = chart;
            }
        }
    }, [state.contentPerformance]);

    // Sentiment Chart
    useEffect(() => {
        if (sentimentChartRef.current && state.sentiment) {
            const ctx = sentimentChartRef.current.getContext('2d');
            if (ctx) {
                if (chartInstancesRef.current.sentiment) {
                    chartInstancesRef.current.sentiment.destroy();
                }

                const chart = new ChartJS(ctx, {
                    type: 'doughnut',
                    data: state.sentiment,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'bottom',
                                labels: {
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    font: {
                                        size: 12
                                    }
                                }
                            }
                        }
                    }
                });

                chartInstancesRef.current.sentiment = chart;
            }
        }
    }, [state.sentiment]);

    // Simple Tooltip component
    const Tooltip: React.FC<{ id: string; content: string; children: React.ReactNode }> = ({ id, content, children }) => (
        <div className="relative inline-block">
            {children}
            {hoveredTooltip === id && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 w-64 text-left">
                    {content}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
            )}
        </div>
    );

    // Cleanup charts on unmount
    useEffect(() => {
        return () => {
            Object.values(chartInstancesRef.current).forEach(chart => {
                if (chart) {
                    chart.destroy();
                }
            });
        };
    }, []);

    // Show account setup if no account exists
    if (!hasAccount) {
        return (
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="w-full max-w-md space-y-6">
                <div className="text-center">
                    <FontAwesomeIcon icon={faTiktok} className="text-6xl text-black mb-6" />
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.tiktok.connectTikTok')}</h1>
                        <p className="text-white/70 text-sm sm:text-base mb-8">
                        {t('socialMedia.tiktok.connectTikTokDescription')}
                    </p>
                    </div>

                    <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                {t('socialMedia.tiktok.tiktokUsername')}
                            </label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faTiktok} className="text-white/40 text-sm" />
                                    </div>
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => {
                                            setUsername(e.target.value);
                                            setVerifyError(null);
                                            setProfile(null);
                                        }}
                                        placeholder={t('socialMedia.engagement.platformPlaceholders.tiktok')}
                                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                                        disabled={verifying || submitting}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !verifying && !submitting) {
                                                handleVerify();
                                            }
                                        }}
                                    />
                                </div>
                                <button
                                    onClick={handleVerify}
                                    disabled={!username.trim() || verifying || submitting}
                                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {verifying ? (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    ) : (
                                        t('socialMedia.common.verify')
                                    )}
                    </button>
                            </div>
                        </div>

                        {verifyError && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                <p className="text-red-400 text-sm">{verifyError}</p>
                            </div>
                        )}

                        {profile && (
                            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                                <div className="flex items-center space-x-3 mb-2">
                                    {profile.profileImageUrl && (
                                        <img
                                            src={profile.profileImageUrl}
                                            alt="Profile"
                                            className="w-12 h-12 rounded-full"
                                            crossOrigin="anonymous"
                                            referrerPolicy="no-referrer"
                                        />
                                    )}
                                    <div>
                                        <p className="text-white font-medium">{profile.displayName || profile.username}</p>
                                        <p className="text-white/70 text-sm">@{profile.username}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 text-sm text-white/70 mt-3">
                                    {profile.followerCount !== undefined && (
                                        <span>{profile.followerCount.toLocaleString()} {t('socialMedia.tiktok.followers')}</span>
                                    )}
                                    {profile.mediaCount !== undefined && (
                                        <span>{profile.mediaCount} {t('socialMedia.tiktok.videos')}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={handleSubmit}
                            disabled={!profile || submitting}
                            className="w-full bg-gradient-to-r from-black to-gray-800 px-6 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center">
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    {t('socialMedia.tiktok.adding')}
                                </span>
                            ) : (
                                t('socialMedia.tiktok.addAndSync')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show pending message if sync is pending
    if (isPending) {
    return (
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-black to-gray-800 flex items-center justify-center mb-6 mx-auto">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                    </div>
                    <FontAwesomeIcon icon={faTiktok} className="text-4xl sm:text-5xl text-black mb-4" />
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.dataPreparing')}</h1>
                    <p className="text-white/70 text-sm sm:text-base mb-6">
                        TikTok {t('socialMedia.common.dataSyncing')}
                    </p>
                    <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
                        <p className="text-white/60 text-xs sm:text-sm">
                            {t('socialMedia.common.autoUpdate')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Show analysis pending screen if sync completed but analysis is pending
    if (isCompleted && isAnalysisPending) {
        return (
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-gray-800 to-black flex items-center justify-center mb-6 mx-auto">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                    </div>
                    <FontAwesomeIcon icon={faTiktok} className="text-4xl sm:text-5xl text-black mb-4" />
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.analysisInProgress')}</h1>
                    <p className="text-white/70 text-sm sm:text-base mb-6">
                        TikTok {t('socialMedia.common.analysisProgress')}
                    </p>
                    <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
                        <p className="text-white/60 text-xs sm:text-sm">
                            {t('socialMedia.common.analysisComplete')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Show analysis start screen if sync completed but no analysis exists
    if (isCompleted && !isAnalysisCompleted && !isAnalysisPending) {
        return (
            <>
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="w-full max-w-md space-y-6">
                    <div className="text-center">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-black to-gray-800 flex items-center justify-center mb-6 mx-auto">
                            <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
                        </div>
                        <FontAwesomeIcon icon={faTiktok} className="text-4xl sm:text-5xl text-black mb-4" />
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.startAiAnalysis')}</h1>
                        <p className="text-white/70 text-sm sm:text-base mb-6">
                            {t('socialMedia.tiktok.dataReady')}
                        </p>
                    </div>

                    <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
                        <div className="space-y-3">
                            <div className="flex items-start space-x-3">
                                <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                                <div>
                                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.aiAnalysisFeatures')}</p>
                                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.detailedPerformance')}</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-3">
                                <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                                <div>
                                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.chartsAndReports')}</p>
                                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.chartsAndReportsDescription')}</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-3">
                                <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                                <div>
                                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.smartRecommendations')}</p>
                                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.smartRecommendationsDescription')}</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={async () => {
                                setAiModalOpen(true);
                                setEstimatedCredits(null);
                                // Set default dates (last 30 days)
                                const today = new Date();
                                const thirtyDaysAgo = new Date();
                                thirtyDaysAgo.setDate(today.getDate() - 30);
                                setEndDate(today.toISOString().split('T')[0]);
                                setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                            }}
                            className="w-full bg-gradient-to-r from-black to-gray-800 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
                        >
                            <FontAwesomeIcon icon={faBrain} className="mr-2" />
                            {t('socialMedia.common.startAiAnalysis')}
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Analysis Modal */}
            {aiModalOpen && (
                <div className="fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
                    <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
                        <div className="p-4 sm:p-6 border-b border-white/10">
                            <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h3>
                        </div>
                        <div className="p-4 sm:p-6 space-y-4">
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.startDate') || 'Başlangıç Tarihi'}</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        max={endDate || new Date().toISOString().split('T')[0]}
                                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.endDate') || 'Bitiş Tarihi'}</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        min={startDate}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                                    />
                                </div>
                            </div>
                            <div className="text-white/80">
                                {t('socialMedia.common.estimatedCredits')}
                                <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                                    {estimateLoading ? (
                                        <span className="flex items-center">
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                            {t('socialMedia.common.calculating')}
                                        </span>
                                    ) : (
                                        estimatedCredits !== null 
                                          ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                                          : '—'
                                    )}
                                </div>
                            </div>
                            <button
                                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!startDate || !endDate}
                                onClick={async () => {
                                    if (!startDate || !endDate) return;
                                    setEstimateLoading(true);
                                    setEstimatedCredits(null);
                                    try {
                                        const res = await creditsAPI.estimate('tiktok', startDate, endDate);
                                        const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                                        setEstimatedCredits(typeof value === 'number' ? value : null);
                                    } finally {
                                        setEstimateLoading(false);
                                    }
                                }}
                            >
                                {t('socialMedia.common.calculateCredits') || 'Kredi Hesapla'}
                            </button>
                            <button
                                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                                onClick={async () => {
                                    try {
                                        if (!startDate || !endDate) return;
                                        setEstimateLoading(true);
                                        await analysisJobsAPI.start('tiktok', startDate, endDate);
                                    } finally {
                                        setEstimateLoading(false);
                                        setAiModalOpen(false);
                                    }
                                }}
                            >
                                {t('socialMedia.common.spend')}
                            </button>
                            <button
                                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                                onClick={() => {
                                    setAiModalOpen(false);
                                    setStartDate('');
                                    setEndDate('');
                                    setEstimatedCredits(null);
                                }}
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6 lg:space-y-8 px-2 md:px-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">{t('socialMedia.tiktok.title')}</h1>
                    <p className="text-gray-400 text-xs sm:text-sm md:text-base">{t('socialMedia.tiktok.subtitle')}</p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 md:gap-4">
                    {business?.tiktok && (
                        <div className={`px-3 py-1 rounded-full text-sm ${
                            business.tiktok.syncStatus === 'completed'
                                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                : business.tiktok.syncStatus === 'pending'
                                    ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                    : business.tiktok.syncStatus === 'failed'
                                        ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                                        : 'bg-white/10 text-white/70 border border-white/20'
                        }`}>
                            {business.tiktok.syncStatus === 'completed' && t('socialMedia.tiktok.syncCompleted')}
                            {business.tiktok.syncStatus === 'pending' && t('socialMedia.tiktok.syncPending')}
                            {business.tiktok.syncStatus === 'failed' && (business.tiktok.syncError ? `${t('socialMedia.tiktok.syncError')} ${business.tiktok.syncError}` : t('socialMedia.tiktok.syncFailed'))}
                            {!business.tiktok.syncStatus && t('socialMedia.tiktok.syncStatusUnknown')}
                            {business.tiktok.syncAt && (
                                <span className="ml-2 opacity-75">
                                    • {new Date(business.tiktok.syncAt).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                                </span>
                            )}
                        </div>
                    )}
                    <button
                        onClick={async () => {
                            setAiModalOpen(true);
                            setEstimatedCredits(null);
                            // Set default dates (last 30 days)
                            const today = new Date();
                            const thirtyDaysAgo = new Date();
                            thirtyDaysAgo.setDate(today.getDate() - 30);
                            setEndDate(today.toISOString().split('T')[0]);
                            setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                        }}
                        disabled={business?.tiktok?.syncStatus !== 'completed'}
                        className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t('socialMedia.common.startAiAnalysis')}
                    </button>
                    <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
                        <FontAwesomeIcon icon={faTiktok} className="text-black text-xl" />
                        <span className="text-white font-semibold">{t('socialMedia.tiktok.connected')}</span>
                    </div>
                </div>
            </div>

            {/* Account Overview */}
            {state.account && (
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
                    <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6">
                        <img
                            src={state.account.profilePicture}
                            alt="Profile"
                            className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full border-2 border-white/20 mx-auto sm:mx-0"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                        />
                        <div className="flex-1 w-full">
                            <div className="flex items-center justify-center sm:justify-start space-x-2 mb-1">
                                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{state.account.username}</h2>
                                {state.account.isVerified && (
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-400 text-sm sm:text-base" />
                                )}
                            </div>
                            {state.account.displayName && (
                                <p className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-3 text-center sm:text-left">{state.account.displayName}</p>
                            )}
                            {state.account.bio && (
                                <p className="text-white/70 mb-2 sm:mb-3 text-xs sm:text-sm text-center sm:text-left">{state.account.bio}</p>
                            )}
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3 md:gap-6 text-white/70 mb-2 sm:mb-3 text-xs sm:text-sm">
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faUsers} />
                                    <span>{(state.account.followers ?? 0).toLocaleString()} {t('socialMedia.tiktok.followers')}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faUser} />
                                    <span>{(state.account.following ?? 0).toLocaleString()} {t('socialMedia.tiktok.following')}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faVideo} />
                                    <span>{state.account.videos} {t('socialMedia.tiktok.videos')}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faHeart} />
                                    <span>{(state.account.totalLikes ?? 0).toLocaleString()} {t('socialMedia.tiktok.likes')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                {/* Engagement Chart */}
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-sm sm:text-base md:text-xl font-semibold text-white">{t('socialMedia.common.engagementAnalysis')}</h3>
                            <Tooltip
                                id="engagement-tooltip"
                                content={t('socialMedia.tiktok.charts.engagementTooltip')}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                                    onMouseEnter={() => handleTooltipEnter('engagement-tooltip')}
                                    onMouseLeave={handleTooltipLeave}
                                />
                            </Tooltip>
                        </div>
                        <div className="flex items-center space-x-3">
                            <FontAwesomeIcon icon={faChartLine} className="text-pink-400 text-lg" />
                            <button
                                onClick={() => setModalChart('engagement')}
                                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                            </button>
                        </div>
                    </div>
                    <div className="h-48 sm:h-64 md:h-80">
                        {state.loading.engagement ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.loading')}</div>
                            </div>
                        ) : state.error.engagement === 'PREPARING' ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-2">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                </div>
                                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingEngagement')}</p>
                                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
                            </div>
                        ) : state.error.engagement ? (
                            <div className="flex items-center justify-center h-full px-2">
                                <div className="text-red-400 text-xs sm:text-sm">{t('socialMedia.common.error')} {state.error.engagement}</div>
                            </div>
                        ) : (
                            <canvas ref={engagementChartRef}></canvas>
                        )}
                    </div>
                    <AIAnalysis chartType="engagement" />
                </div>

                {/* Reach Chart */}
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.common.reachAnalysis')}</h3>
                            <Tooltip
                                id="reach-tooltip"
                                content={t('socialMedia.common.reachDescription')}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                                    onMouseEnter={() => handleTooltipEnter('reach-tooltip')}
                                    onMouseLeave={handleTooltipLeave}
                                />
                            </Tooltip>
                        </div>
                        <div className="flex items-center space-x-3">
                            <FontAwesomeIcon icon={faEye} className="text-blue-400 text-lg" />
                            <button
                                onClick={() => setModalChart('reach')}
                                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                            </button>
                        </div>
                    </div>
                    <div className="h-48 sm:h-56 md:h-64 lg:h-80">
                        {state.loading.reach ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.loading')}</div>
                            </div>
                        ) : state.error.reach === 'PREPARING' ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-2">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                </div>
                                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingReach')}</p>
                                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
                            </div>
                        ) : state.error.reach ? (
                            <div className="flex items-center justify-center h-full px-2">
                                <div className="text-red-400 text-xs sm:text-sm">{t('socialMedia.common.error')} {state.error.reach}</div>
                            </div>
                        ) : (
                            <canvas ref={reachChartRef}></canvas>
                        )}
                    </div>
                    <AIAnalysis chartType="reach" />
                </div>

                {/* Content Performance Chart */}
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.tiktok.charts.contentPerformance')}</h3>
                            <Tooltip
                                id="content-tooltip"
                                content={t('socialMedia.tiktok.charts.contentPerformanceTooltip')}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                                    onMouseEnter={() => handleTooltipEnter('content-tooltip')}
                                    onMouseLeave={handleTooltipLeave}
                                />
                            </Tooltip>
                        </div>
                        <div className="flex items-center space-x-3">
                            <FontAwesomeIcon icon={faVideo} className="text-green-400 text-lg" />
                            <button
                                onClick={() => setModalChart('contentPerformance')}
                                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                            </button>
                        </div>
                    </div>
                    <div className="h-48 sm:h-56 md:h-64 lg:h-80">
                        {state.loading.contentPerformance ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.loading')}</div>
                            </div>
                        ) : state.error.contentPerformance === 'PREPARING' ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-2">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                </div>
                                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingContentPerformance')}</p>
                                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
                            </div>
                        ) : state.error.contentPerformance ? (
                            <div className="flex items-center justify-center h-full px-2">
                                <div className="text-red-400 text-xs sm:text-sm">{t('socialMedia.common.error')} {state.error.contentPerformance}</div>
                            </div>
                        ) : (
                            <canvas ref={contentPerformanceChartRef}></canvas>
                        )}
                    </div>
                    <AIAnalysis chartType="contentPerformance" />
                </div>

                {/* Sentiment Chart */}
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.tiktok.charts.sentimentAnalysis')}</h3>
                            <Tooltip
                                id="sentiment-tooltip"
                                content={t('socialMedia.common.videoReactionsTooltip')}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                                    onMouseEnter={() => handleTooltipEnter('sentiment-tooltip')}
                                    onMouseLeave={handleTooltipLeave}
                                />
                            </Tooltip>
                        </div>
                        <div className="flex items-center space-x-3">
                            <FontAwesomeIcon icon={faHeart} className="text-red-400 text-lg" />
                            <button
                                onClick={() => setModalChart('sentiment')}
                                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                            </button>
                        </div>
                    </div>
                    <div className="h-48 sm:h-56 md:h-64 lg:h-80">
                        {state.loading.sentiment ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.loading')}</div>
                            </div>
                        ) : state.error.sentiment === 'PREPARING' ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-2">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                </div>
                                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingSentiment')}</p>
                                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
                            </div>
                        ) : state.error.sentiment ? (
                            <div className="flex items-center justify-center h-full px-2">
                                <div className="text-red-400 text-xs sm:text-sm">{t('socialMedia.common.error')} {state.error.sentiment}</div>
                            </div>
                        ) : (
                            <canvas ref={sentimentChartRef}></canvas>
                        )}
                    </div>
                    <AIAnalysis chartType="sentiment" />
                </div>

                {/* Hashtags Chart */}
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.tiktok.charts.hashtagAnalysis')}</h3>
                            <Tooltip
                                id="hashtags-tooltip"
                                content={t('socialMedia.tiktok.charts.hashtagTooltip')}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                                    onMouseEnter={() => handleTooltipEnter('hashtags-tooltip')}
                                    onMouseLeave={handleTooltipLeave}
                                />
                            </Tooltip>
                        </div>
                        <div className="flex items-center space-x-3">
                            <FontAwesomeIcon icon={faHashtag} className="text-blue-400 text-lg" />
                            <button
                                onClick={() => setModalChart('hashtags')}
                                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                            </button>
                        </div>
                    </div>
                    <div className="h-48 sm:h-56 md:h-64 lg:h-80">
                    {state.loading.hashtags ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-400"></div>
                        </div>
                    ) : state.error.hashtags === 'PREPARING' ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-2">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                            </div>
                                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingHashtag')}</p>
                                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
                        </div>
                    ) : state.error.hashtags ? (
                            <div className="flex items-center justify-center h-full px-2 text-red-400">
                                <p className="text-xs sm:text-sm">{t('socialMedia.common.error')} {state.error.hashtags}</p>
                        </div>
                    ) : (
                            <canvas ref={hashtagsChartRef}></canvas>
                    )}
                    </div>
                    <AIAnalysis chartType="hashtags" />
                </div>
            </div>

            {/* Problems Section */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-2">
                    <div className="flex items-center space-x-2">
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.tiktok.problems.title')}</h3>
                        <Tooltip id="problems-tooltip" content={t('socialMedia.tiktok.problems.tooltip')}>
                            <FontAwesomeIcon 
                                icon={faInfoCircle} 
                                className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                                onMouseEnter={() => handleTooltipEnter('problems-tooltip')}
                                onMouseLeave={handleTooltipLeave}
                            />
                        </Tooltip>
                    </div>
                </div>
                
                {state.loading.problems ? (
                    <div className="flex items-center justify-center h-32 sm:h-48 md:h-64">
                        <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.tiktok.problems.loading')}</div>
                    </div>
                ) : state.error.problems === 'PREPARING' ? (
                    <div className="flex items-center justify-center h-32 sm:h-48 md:h-64">
                        <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.tiktok.problems.preparing')}</div>
                    </div>
                ) : state.error.problems ? (
                    <div className="flex items-center justify-center h-32 sm:h-48 md:h-64 px-2">
                        <div className="text-red-400 text-xs sm:text-sm text-center">{t('socialMedia.tiktok.problems.error')}</div>
                    </div>
                ) : state.problems && state.problems.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 lg:gap-6">
                        {state.problems.map((problem, index) => (
                            <div key={index} className="backdrop-blur-xl bg-red-500/10 border border-red-500/20 rounded-lg md:rounded-xl p-3 md:p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                        <FontAwesomeIcon icon={faExclamationCircle} className="text-red-400" />
                                        <h4 className="text-white font-semibold">{problem.title}</h4>
                                    </div>
                                    <div className="bg-red-500/20 text-red-400 px-2 py-1 rounded-full text-sm font-medium">
                                        {problem.count} {t('socialMedia.tiktok.problems.times')}
                                    </div>
                                </div>
                                <div className="bg-red-500/10 rounded-lg p-3">
                                    <p className="text-white/80 text-sm">
                                        <span className="font-medium text-red-400">{t('socialMedia.tiktok.problems.recommendation')}</span> {problem.recommendation}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-32 sm:h-48 md:h-64 px-2">
                        <div className="text-center">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-lg sm:text-2xl" />
                            </div>
                            <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.tiktok.problems.noProblems')}</p>
                            <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.tiktok.problems.noProblemsDescription')}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Recent Videos */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 lg:mb-6">{t('socialMedia.tiktok.recentVideos.title')}</h3>
                {state.loading.recentVideos ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="text-white/70 text-sm">{t('socialMedia.tiktok.recentVideos.loading')}</div>
                    </div>
                ) : state.error.recentVideos ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="text-red-400 text-sm">{t('socialMedia.common.error')} {state.error.recentVideos}</div>
                    </div>
                ) : state.recentVideos && state.recentVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
                        {(Array.isArray(state.recentVideos) ? state.recentVideos : []).map((video) => {
                        const formatDuration = (seconds: number) => {
                            if (!seconds) return '';
                            const mins = Math.floor(seconds / 60);
                            const secs = Math.floor(seconds % 60);
                            return `${mins}:${secs.toString().padStart(2, '0')}`;
                        };
                        
                        const videoId = String(video.id);
                        const username = state.account?.username || 'tiktok';
                        
                        return (
                            <div key={video.id} className="group bg-white/5 rounded-lg overflow-hidden hover:bg-white/10 transition-colors">
                                <div className="relative">
                                    <div className="w-full h-48 bg-gradient-to-br from-black/40 to-gray-800/40 flex items-center justify-center relative overflow-hidden">
                                        {video.thumbnail ? (
                                            <img
                                                src={video.thumbnail}
                                                alt="Video thumbnail"
                                                className="w-full h-full object-cover"
                                    crossOrigin="anonymous"
                                                referrerPolicy="no-referrer"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    const fallback = target.parentElement;
                                                    if (fallback) {
                                                        fallback.innerHTML = '<div class="flex flex-col items-center justify-center text-white/50"><svg class="w-16 h-16 mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg><span class="text-sm">Video</span></div>';
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center text-white/50">
                                                <FontAwesomeIcon icon={faVideo} className="text-4xl mb-2" />
                                                <span className="text-sm">Video</span>
                                            </div>
                                        )}
                                    </div>
                                    {video.duration > 0 && (
                                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                            {formatDuration(video.duration)}
                                        </div>
                                    )}
                                </div>
                            <div className="p-4">
                                    <p className="text-white text-sm mb-2 line-clamp-2">{video.caption || t('socialMedia.tiktok.recentVideos.noCaption')}</p>
                                    {video.hashtags && video.hashtags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-3">
                                            {video.hashtags.slice(0, 3).map((hashtag, idx) => (
                                                <span key={idx} className="text-blue-400 text-xs">#{hashtag}</span>
                                            ))}
                                            {video.hashtags.length > 3 && (
                                                <span className="text-gray-400 text-xs">+{video.hashtags.length - 3}</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                                        <div className="flex items-center space-x-3">
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faHeart} />
                                            <span>{(video.likes ?? 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faComment} />
                                            <span>{(video.comments ?? 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faShare} />
                                            <span>{(video.shares ?? 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <FontAwesomeIcon icon={faEye} />
                                        <span>{(video.views ?? 0).toLocaleString()}</span>
                                    </div>
                                </div>
                                    {video.postedAt && (
                                        <p className="text-gray-500 text-xs">{video.postedAt}</p>
                                    )}
                            </div>
                        </div>
                        );
                    })}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-48">
                        <div className="text-white/70 text-sm">{t('socialMedia.tiktok.recentVideos.noVideos')}</div>
                    </div>
                )}
            </div>

            {/* Top Videos */}
            {state.topVideos && state.topVideos.length > 0 && (
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-3 md:mb-4 lg:mb-6">{t('socialMedia.tiktok.topVideos.title')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
                        {(Array.isArray(state.topVideos) ? state.topVideos : []).map((video) => {
                            const formatDuration = (seconds: number) => {
                                if (!seconds) return '';
                                const mins = Math.floor(seconds / 60);
                                const secs = Math.floor(seconds % 60);
                                return `${mins}:${secs.toString().padStart(2, '0')}`;
                            };
                            
                            const videoId = String(video.id);
                            const username = state.account?.username || 'tiktok';
                            
                            return (
                                <div key={video.id} className="group bg-white/5 rounded-lg overflow-hidden hover:bg-white/10 transition-colors">
                                    <div className="relative">
                                        <div className="w-full h-48 bg-gradient-to-br from-black/40 to-gray-800/40 flex items-center justify-center relative overflow-hidden">
                                            {video.thumbnail ? (
                                <img
                                    src={video.thumbnail}
                                    alt="Video thumbnail"
                                                    className="w-full h-full object-cover"
                                    crossOrigin="anonymous"
                                    referrerPolicy="no-referrer"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        const fallback = target.parentElement;
                                                        if (fallback) {
                                                            fallback.innerHTML = '<div class="flex flex-col items-center justify-center text-white/50"><svg class="w-16 h-16 mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg><span class="text-sm">Video</span></div>';
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div className="flex flex-col items-center justify-center text-white/50">
                                                    <FontAwesomeIcon icon={faVideo} className="text-4xl mb-2" />
                                                    <span className="text-sm">Video</span>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                                    <FontAwesomeIcon icon={faVideo} className="text-white text-2xl" />
                                                </div>
                                            </div>
                                        </div>
                                        {video.duration > 0 && (
                                            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                {formatDuration(video.duration)}
                                            </div>
                                        )}
                                    </div>
                            <div className="p-4">
                                        <p className="text-white text-sm mb-2 line-clamp-2">{video.caption || t('socialMedia.tiktok.recentVideos.noCaption')}</p>
                                        {video.hashtags && video.hashtags.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mb-3">
                                                {video.hashtags.slice(0, 3).map((hashtag, idx) => (
                                                    <span key={idx} className="text-blue-400 text-xs">#{hashtag}</span>
                                                ))}
                                                {video.hashtags.length > 3 && (
                                                    <span className="text-gray-400 text-xs">+{video.hashtags.length - 3}</span>
                                                )}
                </div>
                                        )}
                                        <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                                            <div className="flex items-center space-x-3">
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faHeart} />
                                            <span>{(video.likes ?? 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faComment} />
                                            <span>{(video.comments ?? 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faShare} />
                                            <span>{(video.shares ?? 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <FontAwesomeIcon icon={faEye} />
                                        <span>{(video.views ?? 0).toLocaleString()}</span>
                                    </div>
                                </div>
                                        {video.postedAt && (
                                            <p className="text-gray-500 text-xs">{video.postedAt}</p>
                                        )}
                            </div>
                        </div>
                            );
                        })}
                </div>
            </div>
            )}
            {aiModalOpen && (
            <div className="fixed inset-0 z-50">
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-900 rounded-xl md:rounded-2xl border border-white/10 w-[90%] sm:w-full max-w-md overflow-hidden">
                    <div className="p-4 sm:p-5 md:p-6 border-b border-white/10">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h3>
                    </div>
                    <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.startDate') || 'Başlangıç Tarihi'}</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={endDate || new Date().toISOString().split('T')[0]}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.endDate') || 'Bitiş Tarihi'}</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                                />
                            </div>
                        </div>
                        <div className="text-white/80">
                            {t('socialMedia.common.estimatedCredits')}
                            <div className="mt-2 text-2xl font-semibold text-white">
                                {estimateLoading ? t('socialMedia.common.calculating') : (estimatedCredits !== null 
                                  ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                                  : '—')}
                            </div>
                        </div>
                        <button
                            className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!startDate || !endDate}
                            onClick={async () => {
                                if (!startDate || !endDate) return;
                                setEstimateLoading(true);
                                setEstimatedCredits(null);
                                try {
                                    const res = await creditsAPI.estimate('tiktok', startDate, endDate);
                                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                                    setEstimatedCredits(typeof value === 'number' ? value : null);
                                } finally {
                                    setEstimateLoading(false);
                                }
                            }}
                        >
                            {t('socialMedia.common.calculateCredits') || 'Kredi Hesapla'}
                        </button>
                        <button
                            className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                            onClick={async () => {
                                try {
                                    if (!startDate || !endDate) return;
                                    setEstimateLoading(true);
                                    await analysisJobsAPI.start('tiktok', startDate, endDate);
                                } finally {
                                    setEstimateLoading(false);
                                    setAiModalOpen(false);
                                }
                            }}
                        >
                            {t('socialMedia.common.spend')}
                        </button>
                        <button
                            className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                            onClick={() => {
                                setAiModalOpen(false);
                                setStartDate('');
                                setEndDate('');
                                setEstimatedCredits(null);
                            }}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
            )}

        </div>
    );
};

export default TikTokPage;
